
import React from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import BackToTop from "@/components/ui/BackToTop";
import { useCountriesWithRegions } from "@/hooks/useCountriesWithRegions";
import { useNavigate } from "react-router-dom";
import CountryFilters, { CountryFilterType, ViewType } from "@/components/country/CountryFilters";
import { useState } from "react";
import CountryRegionalView from "@/components/country/CountryRegionalView";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Clock } from "lucide-react";
import CountryFlag from "@/components/country/CountryFlag";

const PollsCountrySelector = () => {
  const navigate = useNavigate();
  const { countries, isLoading } = useCountriesWithRegions();
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState<CountryFilterType>("active");
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [viewType, setViewType] = useState<ViewType>("list");

  const handleSelectCountry = (countryCode: string) => {
    navigate(`/polls/${countryCode}`);
  };

  // Filter countries based on search and filters
  const filteredCountries = countries.filter(country => {
    const matchesSearch = country.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filter === "all" || 
      (filter === "active" && country.active) || 
      (filter === "coming-soon" && !country.active);
    const matchesRegion = !selectedRegion || 
      selectedRegion === "no-region" && !country.region ||
      country.region?.id === selectedRegion;
    
    return matchesSearch && matchesFilter && matchesRegion;
  });

  // Extract available regions from countries data
  const availableRegions = React.useMemo(() => {
    console.log('🔍 PollsCountrySelector - countries data:', countries);
    const regionsMap = new Map();
    countries.forEach(country => {
      console.log('🔍 Processing country:', country.name, 'region:', country.region);
      if (country.region) {
        regionsMap.set(country.region.id, country.region);
      }
    });
    const regions = Array.from(regionsMap.values());
    console.log('🔍 PollsCountrySelector - availableRegions:', regions);
    return regions;
  }, [countries]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-12 pt-24">
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin h-8 w-8 border-4 border-nation-blue border-t-transparent rounded-full"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-12 pt-24">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h1 className="text-3xl md:text-4xl font-bold text-nation-blue mb-4">
              Public Polls by Country
            </h1>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Select a country to view public polls and participate in citizen surveys.
            </p>
          </div>

          <CountryFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            filter={filter}
            onFilterChange={setFilter}
            selectedRegion={selectedRegion}
            onRegionChange={setSelectedRegion}
            viewType={viewType}
            onViewTypeChange={setViewType}
            availableRegions={availableRegions}
          />

          {viewType === "region" ? (
            <CountryRegionalView 
              countries={filteredCountries}
              onSelectCountry={handleSelectCountry}
            />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCountries.map(country => (
                <Card key={country.code} className={country.active ? 'border-nation-teal' : 'border-gray-200'}>
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <CountryFlag countryCode={country.code} className="h-5 w-6" />
                        <CardTitle className="text-lg font-semibold text-nation-blue">
                          {country.name}
                        </CardTitle>
                      </div>
                      {country.active ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center">
                          <Check size={12} className="mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded-full flex items-center">
                          <Clock size={12} className="mr-1" />
                          Coming Soon
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {country.active ? (
                      <Button 
                        variant="default" 
                        className="w-full bg-nation-teal hover:bg-nation-teal-dark"
                        onClick={() => handleSelectCountry(country.code)}
                      >
                        View Polls
                      </Button>
                    ) : (
                      <Button variant="outline" className="w-full" disabled>
                        Not Available Yet
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {filteredCountries.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No countries found matching your criteria.</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default PollsCountrySelector;
